import React, { useEffect, useMemo, useState, memo, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core";
import { Calendar, ChevronLeft, ChevronRight, Users, Clock, GripVertical, Briefcase, Sparkles, Search } from "lucide-react";
import DispatchSuggest from "@/components/DispatchSuggest";

const HOURS = Array.from({ length: 10 }, (_, i) => 8 + i); // 8am - 5pm
const DAY_MS = 86_400_000;
const TRAY_PAGE_SIZE = 20;

const fmtDay = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const isoDay = (d) => d.toISOString().slice(0, 10);

export default function AdminDispatch() {
  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0 = Sun
    d.setDate(d.getDate() - day + 1); // Monday
    return d;
  });
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    try {
      const [w, e] = await Promise.all([api.get("/work-orders"), api.get("/employees")]);
      setWorkOrders(w.data);
      setEmployees(e.data);
    } catch (err) {
      console.warn("Dispatch load failed:", err);
    }
  };
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  // Build a quick map: slotId = `${employeeId}|${YYYY-MM-DD}|${hour}` -> WO
  const slotMap = useMemo(() => {
    const m = {};
    workOrders.forEach((w) => {
      if (!w.scheduled_at) return;
      const dt = new Date(w.scheduled_at);
      if (Number.isNaN(dt.getTime())) return;
      const dayKey = isoDay(dt);
      const hour = dt.getHours();
      if (w.assigned_employee_id) {
        m[`${w.assigned_employee_id}|${dayKey}|${hour}`] = w;
      }
    });
    return m;
  }, [workOrders]);

  // Unscheduled tray
  const unscheduled = useMemo(
    () => workOrders.filter((w) => !w.scheduled_at && w.status !== "completed"),
    [workOrders],
  );

  const moveWO = async (woId, employeeId, day, hour) => {
    const wo = workOrders.find((w) => w.work_order_id === woId);
    if (!wo) return;
    const newDate = new Date(day);
    newDate.setHours(hour, 0, 0, 0);
    const prev = { scheduled_at: wo.scheduled_at, assigned_employee_id: wo.assigned_employee_id };
    setWorkOrders((arr) => arr.map((w) => w.work_order_id === woId ? { ...w, scheduled_at: newDate.toISOString(), assigned_employee_id: employeeId } : w));
    try {
      await api.patch(`/work-orders/${woId}`, {
        scheduled_at: newDate.toISOString(),
        assigned_employee_id: employeeId,
      });
      toast.success("Scheduled");
    } catch (err) {
      setWorkOrders((arr) => arr.map((w) => w.work_order_id === woId ? { ...w, ...prev } : w));
      toast.error("Couldn't schedule");
    }
  };

  const unschedule = async (woId) => {
    const wo = workOrders.find((w) => w.work_order_id === woId);
    if (!wo) return;
    const prev = { scheduled_at: wo.scheduled_at };
    setWorkOrders((arr) => arr.map((w) => w.work_order_id === woId ? { ...w, scheduled_at: null } : w));
    try {
      await api.patch(`/work-orders/${woId}`, { scheduled_at: null });
    } catch (err) {
      setWorkOrders((arr) => arr.map((w) => w.work_order_id === woId ? { ...w, ...prev } : w));
      toast.error("Couldn't unschedule");
    }
  };

  // Smart-suggest path: drop on the next available 9am slot for the chosen crew.
  const assignSuggested = async (woId, employeeId) => {
    // Find next weekday 9am
    const next = new Date();
    next.setHours(9, 0, 0, 0);
    if (next < new Date()) next.setDate(next.getDate() + 1);
    while (next.getDay() === 0 || next.getDay() === 6) next.setDate(next.getDate() + 1);
    await moveWO(woId, employeeId, next, 9);
  };

  const onDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    if (over.id === "tray") {
      unschedule(active.id);
      return;
    }
    const [employeeId, dayKey, hourStr] = over.id.split("|");
    moveWO(active.id, employeeId, new Date(dayKey), Number(hourStr));
  };

  const dragged = workOrders.find((w) => w.work_order_id === activeId);

  return (
    <div data-testid="admin-dispatch">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="overline">Field operations</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Dispatch board</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">Drag a work order onto a crew × time slot to schedule it.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart((d) => new Date(d.getTime() - 7 * DAY_MS))} className="btn-outline-navy text-sm py-2 px-3" data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-sm text-[#0A2540] dark:text-white font-medium tabular-nums">
            {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – {days[4].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
          <button onClick={() => setWeekStart((d) => new Date(d.getTime() + 7 * DAY_MS))} className="btn-outline-navy text-sm py-2 px-3" data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => {
            const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - d.getDay() + 1);
            setWeekStart(d);
          }} className="btn-accent text-sm py-2 px-3" data-testid="week-today">Today</button>
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={onDragEnd}>
        <div className="grid lg:grid-cols-[260px_1fr] gap-4">
          {/* Unscheduled tray */}
          <Tray workOrders={unscheduled} onSuggestAssign={assignSuggested} />

          {/* Schedule grid */}
          <div className="card-pb p-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left p-2 sticky left-0 bg-white dark:bg-slate-900 z-10">
                    <span className="overline">Crew</span>
                  </th>
                  {days.map((d) => HOURS.map((h) => (
                    <th key={`${isoDay(d)}|${h}`} className="text-center p-1 font-normal text-[10px] text-[#94A3B8]">
                      <div className="text-[#0A2540] dark:text-white font-medium">{h % 12 || 12}{h < 12 ? "a" : "p"}</div>
                    </th>
                  )))}
                </tr>
                <tr>
                  <th className="sticky left-0 bg-white dark:bg-slate-900 z-10 p-0"></th>
                  {days.map((d) => (
                    <th key={isoDay(d)} colSpan={HOURS.length} className="border-b border-[#E2E8F0] dark:border-slate-700 p-2 text-left">
                      <span className="text-xs font-medium text-[#0A2540] dark:text-white">{fmtDay(d)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.length === 0 ? (
                  <tr><td colSpan={1 + days.length * HOURS.length} className="text-center text-[#94A3B8] py-8">No employees yet</td></tr>
                ) : employees.map((emp) => (
                  <tr key={emp.user_id}>
                    <td className="p-2 sticky left-0 bg-white dark:bg-slate-900 z-10 border-r border-[#E2E8F0] dark:border-slate-700 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#C9A356]/15 text-[#C9A356] grid place-items-center text-xs font-bold">
                          {(emp.name || "?").slice(0, 1)}
                        </div>
                        <span className="text-xs font-medium text-[#0A2540] dark:text-white">{emp.name?.split(" ")[0]}</span>
                      </div>
                    </td>
                    {days.map((d) => HOURS.map((h) => {
                      const slotId = `${emp.user_id}|${isoDay(d)}|${h}`;
                      const wo = slotMap[slotId];
                      return <SlotCell key={slotId} slotId={slotId} wo={wo} />;
                    }))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <DragOverlay>
          {dragged ? <WOCardCompact wo={dragged} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function Tray({ workOrders, onSuggestAssign }) {
  const { setNodeRef, isOver } = useDroppable({ id: "tray" });
  const [suggestForId, setSuggestForId] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  // Reset pagination when the query changes so the newest match is on-screen.
  useEffect(() => { setPage(1); }, [query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workOrders;
    return workOrders.filter((w) =>
      (w.title || "").toLowerCase().includes(q) ||
      (w.customer_code || "").toLowerCase().includes(q) ||
      (w.work_order_number || "").toLowerCase().includes(q),
    );
  }, [workOrders, query]);

  const visible = useMemo(
    () => filtered.slice(0, page * TRAY_PAGE_SIZE),
    [filtered, page],
  );
  const hasMore = visible.length < filtered.length;

  return (
    <div ref={setNodeRef}
         className={`card-pb p-3 max-h-[70vh] overflow-y-auto ${isOver ? "ring-2 ring-[#C9A356]" : ""}`}
         data-testid="unscheduled-tray">
      <div className="flex items-center gap-2 mb-2">
        <Briefcase className="w-4 h-4 text-[#C9A356]" />
        <span className="overline">Unscheduled ({filtered.length}{query ? ` of ${workOrders.length}` : ""})</span>
      </div>
      <div className="relative mb-3">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by title, PB-, or WO#"
          data-testid="tray-filter"
          className="w-full text-xs pl-7 pr-2 py-1.5 rounded border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#0A2540] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#C9A356]"
        />
      </div>
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-xs text-[#94A3B8] text-center py-6">
            {query ? "No matches" : "All work orders scheduled ✓"}
          </div>
        ) : visible.map((w) => (
          <div key={w.work_order_id} className="relative">
            <WOCardDraggable wo={w} />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSuggestForId(suggestForId === w.work_order_id ? null : w.work_order_id); }}
              data-testid={`wo-suggest-${w.work_order_id}`}
              className="absolute top-1 right-1 w-6 h-6 rounded-md bg-[#C9A356]/20 hover:bg-[#C9A356] text-[#C9A356] hover:text-white grid place-items-center transition"
              title="Suggest crew"
            >
              <Sparkles className="w-3 h-3" />
            </button>
            {suggestForId === w.work_order_id && (
              <DispatchSuggest
                workOrderId={w.work_order_id}
                onClose={() => setSuggestForId(null)}
                onAssign={async (employeeId) => {
                  await onSuggestAssign(w.work_order_id, employeeId);
                  setSuggestForId(null);
                }}
              />
            )}
          </div>
        ))}
        {hasMore && (
          <button
            onClick={() => setPage((p) => p + 1)}
            data-testid="tray-load-more"
            className="w-full text-xs py-2 rounded border border-dashed border-[#E2E8F0] dark:border-slate-700 text-[#64748B] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/60"
          >
            Load {Math.min(TRAY_PAGE_SIZE, filtered.length - visible.length)} more · {filtered.length - visible.length} remaining
          </button>
        )}
      </div>
    </div>
  );
}

const SlotCell = memo(function SlotCell({ slotId, wo }) {
  const { setNodeRef, isOver } = useDroppable({ id: slotId });
  return (
    <td ref={setNodeRef}
        className={`p-0.5 border border-dashed border-[#E2E8F0] dark:border-slate-700 min-w-[60px] h-14 align-top ${isOver ? "bg-[#FFFBF0] dark:bg-amber-900/10" : ""}`}>
      {wo ? <WOCardDraggable wo={wo} compact /> : null}
    </td>
  );
});

function WOCardDraggable({ wo, compact = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: wo.work_order_id });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.3 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
         className={`rounded ${compact ? "px-1.5 py-1" : "p-2"} bg-[#0A2540] text-white cursor-grab active:cursor-grabbing border border-[#C9A356]/40`}
         data-testid={`wo-${wo.work_order_id}`}>
      <div className={`${compact ? "text-[10px]" : "text-xs"} font-medium truncate`}>{wo.title}</div>
      {!compact && <div className="text-[10px] text-[#C9A356] mt-0.5 truncate">{wo.customer_code}</div>}
    </div>
  );
}

function WOCardCompact({ wo }) {
  return (
    <div className="bg-[#C9A356] text-white rounded p-2 shadow-2xl text-xs font-medium rotate-2 max-w-[180px]">
      {wo.title}
    </div>
  );
}
